import React from "react";

import { Intro, Text } from "components/commons";
import useDataTable from "hooks/useDataTable";
import locale from "localization";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import { getMerchantStationPayments, voidPayment } from "apis/payment.api";
import { nonFuelFilterState } from "./non-fuel-filter.state";
import { mapDataToList, mapFilterToRequest } from "./non-fuel.mapper";
import { nonFuelColumns } from "./non-fuel-columns";
import NonFuelFilter from "./non-fuel-filter";
import { useContext } from "react";
import { StationContext } from "contexts";
import { useApi, useExport, useModal } from "hooks";
import { generateStationReport } from "apis/generate-report.api";
import { SuccessModal } from "components/modals";
import { SettlementStatus } from "enums";
import { formatAmount } from "utils";
import { useCallback } from "react";
import { useState } from "react";
import VoidPaymentModal from "./void-payment-modal";
import ActionsHistoryModal from "./actions-history-modal";

const NonFuelModule = () => {
  const voidPaymentModal = useModal();
  const actionsHistoryModal = useModal();
  const confirmModal = useModal();

  const { station } = useContext(StationContext);
  const [remarks, setRemarks] = useState("");
  const [value, setValue] = useState("");

  const { request: voidRequest, loading: voidingRequest } = useApi({
    api: voidPayment,
    pageError: false,
    modalError: true,
  });

  const handleVoidPayment = ({ merchantPaymentId, amount, settlementId, settlementStatus }) => {
    setRemarks("");
    voidPaymentModal.show({
      title: `${locale.voidTransaction}?`,
      description: (
        <Text align="center">
          <locale.Populate
            text={
              settlementId
                ? settlementStatus === SettlementStatus.ForProcessing
                  ? locale.voidingThisTransactionSettlementProcessing
                  : locale.voidingThisTransactionSettlementProcessed
                : locale.voidingThisPayment
            }
            items={[<b>{formatAmount(amount)}</b>, <b>{formatAmount(0)}</b>]}
          />
        </Text>
      ),
      label: locale.reasonRemarks,
      actionText: locale.voidTransaction,
      submit: (value) => {
        onVoidPaymentCb({
          merchantPaymentId,
          value,
          amount,
        });
      },
    });
  };

  const handleActionsHistory = ({ voidedByEmail, status, voidedAt }) => {
    actionsHistoryModal.show({
      title: locale.actionsHistory,
      voidedByEmail,
      status,
      voidedAt,
    });
  };

  const onVoidPaymentCb = useCallback(
    async ({ merchantPaymentId, value, amount }) => {
      await voidRequest({ merchantPaymentId, remarks: value });
      voidPaymentModal.close();
      table.fetch({});

      confirmModal.show({
        title: locale.transactionVoided,
        content: (
          <locale.Populate
            text={locale.thisPaymentHasBeenVoided}
            items={[<b>{formatAmount(amount)}</b>]}
          />
        ),
        loading: voidingRequest,
        primary: {
          text: locale.gotIt,
          onClick: () => {
            confirmModal.close();
          },
        },
      });
      setRemarks("");
    },
    //eslint-disable-next-line
    [voidRequest, voidPaymentModal, confirmModal, voidingRequest]
  );

  const { filter, search, table } = useDataTable({
    api: { api: getMerchantStationPayments, params: { stationId: station.stationId } },
    filter: { initialState: nonFuelFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "merchantPayments",
      mapper: (merchantPayments) => mapDataToList({ merchantPayments, handleVoidPayment, handleActionsHistory }),
      columns: nonFuelColumns,
    },
  });

  const exports = useExport({
    api: generateStationReport,
    hasModal: true,
    reportType: "merchant-payment",
    mappedFilterState: {
      ...filter?.mappedFilterState,
      merchantPaymentStatuses: filter?.mappedFilterState.status,
      searchKey: search.searchKey,
      stationId: station.stationId,
      redemptionStationId: station.stationId,
    },
  });

  delete table.result.count;

  return (
    <>
      <SuccessModal {...exports?.successModalComponent} />
      <div>
        <Intro title={locale.locqPayForNonFuel} subtitle={locale.viewAndTrackScanToPayViaLocqPay} />
      </div>
      <NonFuelFilter {...filter} {...search} {...exports} />
      <DataTableV2 {...table}/>
      <ActionsHistoryModal {...actionsHistoryModal} />
      <VoidPaymentModal
        {...voidPaymentModal}
        remarks={remarks}
        onChangeRemarks={(v) => {
          setRemarks(v);
        }}
        loading={voidingRequest}
        onSubmit={voidPaymentModal.submit}
        value={value}
        setValue={setValue}
      />
    </>
  );
};

export default NonFuelModule;
